'use client'

import { useCallback, useMemo } from 'react'
import { useSession } from 'next-auth/react'

import type { DeliveryAddressPort } from 'modules/customer'
import { queryFetchDeliveryMethods } from 'modules/delivery'
import { FormMode } from 'shared/types/forms'
import { noop } from 'shared/utils/common'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import useCartStore from '../../store/cartStore'

const checkDeliveryCoverage = async (postalCode: string, city: string) =>
  isNotNilOrEmpty(await queryFetchDeliveryMethods({ postalCode, city }))

export const useDeliveryAddressAdapter = (): DeliveryAddressPort => {
  const session = useSession()
  const { addAddressToCart, cart } = useCartStore()

  // A new address replaces the one in the cart; an edited one is sent again
  // when it is the cart's address.
  const onDeliveryAddressSaved = useCallback(
    (addressId: string, mode: FormMode) => {
      const isCartAddress = addressId === cart?.deliveryAddressId
      const shouldUpdateCart =
        mode === FormMode.create
          ? !!cart?.deliveryAddressId && !isCartAddress
          : isCartAddress

      if (shouldUpdateCart) {
        addAddressToCart(addressId, session).then(noop).catch(noop)
      }
    },
    [addAddressToCart, cart?.deliveryAddressId, session]
  )

  return useMemo(
    () => ({ checkDeliveryCoverage, onDeliveryAddressSaved }),
    [onDeliveryAddressSaved]
  )
}
